/**
 * Wastage EOM — page breaking.
 *
 * The same machinery as the Expense EOM (expense_eom.pages.js, on the Sales EOM's A4 sheet):
 * a long section is a FLOW of items with estimated heights, laid onto as many A4 pages as it
 * needs. A table that resumes on a new page repeats its column headers, and the page reads
 * "(CONT.)". The report then measures every page in the browser and pushes items forward off
 * any page that overflows, so a short estimate costs a layout pass, never a sliced page.
 *
 * One addition: a flow may hold more than one titled section — Part 2 and Part 3 share a
 * sheet, as they do in the reference report. A section item that opens a page becomes that
 * page's header; one that lands mid-page is drawn as a heading in the body.
 *
 * Pure functions. No React, no DOM.
 */
import { A4_RATIO, FOOTER_RESERVE, PAGE_BUDGET, paginateFlow } from './expense_eom.pages';

export { A4_RATIO, FOOTER_RESERVE, PAGE_BUDGET, paginateFlow };

// Estimated against wastage_eom.css at the 960px page width.
export const H = {
  header: 92, // eyebrow + page title
  ledeLine: 22,
  ledePad: 18,
  ledeChars: 112,
  contLede: 40, // "Continued from the previous page."
  sectionGap: 44, // a second titled section's top margin
  quadHead: 96, // Q heading + its rule + the Action Rule line
  tableHead: 72, // a header row on one line, plus the table's top margin
  tableHead2: 90, // header cells that wrap to two lines
  row: 39,
  rowLine: 17, // each extra line a wrapped cell adds
  totalRow: 46,
  noteLine: 19,
  notePad: 18,
  noteChars: 130,
  notice: 210,
  eventHead: 46,
};

const linesOf = (text, perLine) => (text ? Math.max(1, Math.ceil(String(text).length / perLine)) : 0);

export const ledeHeight = (text) => (text ? H.ledePad + linesOf(text, H.ledeChars) * H.ledeLine : 0);
export const noteHeight = (text) => (text ? H.notePad + linesOf(text, H.noteChars) * H.noteLine : 0);
/** A table row whose widest cell wraps: `chars` is what fits on one line of that cell. */
export const rowHeight = (text, chars) => H.row + Math.max(0, linesOf(text, chars) - 1) * H.rowLine;
/** A titled section: as a page header it is the header; mid-page it also pays its top gap. */
export const sectionHeight = (lede) => H.header + ledeHeight(lede);

/**
 * Lay a flow whose FIRST item is its opening section. The first page pays nothing extra (the
 * section item is the header); later pages reserve a "(CONT.)" header. A later page that
 * happens to open on a new section over-reserves by that much — white space, never overflow.
 */
export const paginateSections = (items, budget, tableHead) =>
  paginateFlow(items, { firstHeader: 0, contHeader: H.header + H.contLede, budget, tableHead });

/**
 * The header a page shows: the section it opens with, or — if it opens mid-section — the last
 * section before it, marked continued. `body` is what remains to draw under that header.
 */
export function pageHeader(items, from, to) {
  const first = items[from];
  if (first && first.section) {
    return { section: first.section, continued: false, body: items.slice(from + 1, to) };
  }
  let prev = null;
  for (let i = from - 1; i >= 0; i -= 1) {
    if (items[i].section) {
      prev = items[i].section;
      break;
    }
  }
  return { section: prev || { eyebrow: '', title: '' }, continued: true, body: items.slice(from, to) };
}
